import {
  useArchiveCannedWorkPackagesMutation,
  useRestoreCannedWorkPackagesMutation,
} from "@common/api/quoting/canned"
import type { CannedWorkPackageDetailPublic } from "@common/client"
import { formatCount } from "@common/lib/number"
import { handleError } from "@common/utils"
import { useNavigate } from "@tanstack/react-router"
import {
  FloatingAction,
  FloatingActionBar,
} from "@/components/layout/floating-action-bar"
import useCustomToast from "@/hooks/use-custom-toast"
import DeleteOutline from "~icons/material-symbols/delete-outline"

function counted(count: number): string {
  return `${formatCount(count)} template quote${count === 1 ? "" : "s"}`
}

export function QuoteSelectionBar({
  quotes,
  onClear,
}: {
  quotes: CannedWorkPackageDetailPublic[]
  onClear: () => void
}): React.ReactElement {
  const navigate = useNavigate()
  const archiveMany = useArchiveCannedWorkPackagesMutation()
  const restoreMany = useRestoreCannedWorkPackagesMutation()
  const showToast = useCustomToast()
  const live = quotes.filter((item) => !item.archived_at)
  const archived = quotes.filter((item) => item.archived_at)
  const bulkIds = (targets: CannedWorkPackageDetailPublic[]) => ({
    cannedWorkPackageBulkIds: {
      canned_work_package_ids: targets.map((item) => item.id),
    },
  })
  const done = (title: string, count: number, skipped: number) => {
    showToast(
      title,
      `${title} ${counted(count)}.`,
      skipped > 0 ? "error" : "success",
    )
    onClear()
  }

  return (
    <FloatingActionBar
      count={quotes.length}
      archive={{
        count: live.length,
        pending: archiveMany.isPending,
        onClick: () =>
          archiveMany.mutate(bulkIds(live), {
            onSuccess: (result) =>
              done("Archived", result.archived.length, result.skipped.length),
            onError: (error) => handleError(error, showToast),
          }),
      }}
      restore={{
        count: archived.length,
        pending: restoreMany.isPending,
        onClick: () =>
          restoreMany.mutate(bulkIds(archived), {
            onSuccess: (result) =>
              done("Restored", result.restored.length, result.skipped.length),
            onError: (error) => handleError(error, showToast),
          }),
      }}
      onClear={onClear}
    >
      <FloatingAction
        hint={`Delete ${quotes.length}`}
        onClick={() =>
          navigate({
            from: "/templates/quotes",
            to: "/templates/quotes/delete",
            search: (previous) => ({
              ...previous,
              ids: quotes.map((item) => item.id),
            }),
          })
        }
      >
        <DeleteOutline />
        Delete
      </FloatingAction>
    </FloatingActionBar>
  )
}
